import { apiClient } from './apiClient';

class PDFExportService {
  constructor() {
    this.baseUrl = `${apiClient.baseUrl}/pdf-export`;
  }

  // Hilfsfunktion für API-Requests
  async makeRequest(endpoint, options = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;
      const config = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      };

      const response = await fetch(url, config);
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      return response;
    } catch (error) {
      console.error('API Request Error:', error);
      throw error;
    }
  }

  // Exportiert gefilterte Teilnehmerliste als PDF
  async exportParticipantsPDF(filters = {}) {
    try {
      // Baue Query-Parameter aus Filtern
      const queryParams = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }
      if (filters.ageGroup && filters.ageGroup !== 'all') {
        queryParams.append('ageGroup', filters.ageGroup);
      }
      if (filters.nationality && filters.nationality !== 'all') {
        queryParams.append('nationality', filters.nationality);
      }
      if (filters.gender && filters.gender !== 'all') {
        queryParams.append('gender', filters.gender);
      }
      if (filters.searchTerm) {
        queryParams.append('searchTerm', filters.searchTerm);
      }

      const endpoint = `/participants${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await this.makeRequest(endpoint);

      // Erstelle Blob aus Response
      const blob = await response.blob();
      
      // Erstelle Download-Link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extrahiere Dateiname aus Response-Header oder verwende Standard
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'teilnehmerliste.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: 'PDF erfolgreich heruntergeladen'
      };

    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim PDF-Export'
      };
    }
  }

  // Exportiert gefilterte Teilnehmerliste als JSON
  async exportParticipantsJSON(filters = {}) {
    try {
      // Baue Query-Parameter aus Filtern
      const queryParams = new URLSearchParams();
      
      if (filters.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }
      if (filters.ageGroup && filters.ageGroup !== 'all') {
        queryParams.append('ageGroup', filters.ageGroup);
      }
      if (filters.nationality && filters.nationality !== 'all') {
        queryParams.append('nationality', filters.nationality);
      }
      if (filters.gender && filters.gender !== 'all') {
        queryParams.append('gender', filters.gender);
      }
      if (filters.searchTerm) {
        queryParams.append('searchTerm', filters.searchTerm);
      }

      const endpoint = `/participants/json${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await this.makeRequest(endpoint);

      const data = await response.json();
      
      // Erstelle JSON-Datei
      const jsonString = JSON.stringify(data.data || data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Erstelle Download-Link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Erstelle Dateiname mit Timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `teilnehmerliste_${timestamp}.json`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: 'JSON erfolgreich heruntergeladen'
      };

    } catch (error) {
      console.error('Fehler beim JSON-Export:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim JSON-Export'
      };
    }
  }

  // Importiert Teilnehmer aus JSON
  async importParticipantsJSON(participantsArray) {
    try {
      // Import-Endpoint ist unter /api/participants/import, nicht unter /api/pdf-export
      const url = `${apiClient.baseUrl}/participants/import`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ participants: participantsArray })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.data,
        message: data.message || 'JSON erfolgreich importiert'
      };

    } catch (error) {
      console.error('Fehler beim JSON-Import:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim JSON-Import'
      };
    }
  }

  // Exportiert gefilterte Zeitplan-Daten als PDF
  async exportSchedulePDF(filters = {}) {
    try {
      // Baue Query-Parameter aus Filtern
      const queryParams = new URLSearchParams();
      
      if (filters.day && filters.day !== 'all') {
        queryParams.append('day', filters.day);
      }
      if (filters.category && filters.category !== 'all') {
        queryParams.append('category', filters.category);
      }
      if (filters.status && filters.status !== 'all') {
        queryParams.append('status', filters.status);
      }
      if (filters.gender && filters.gender !== 'all') {
        queryParams.append('gender', filters.gender);
      }
      if (filters.searchTerm) {
        queryParams.append('searchTerm', filters.searchTerm);
      }

      const endpoint = `/schedule${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await this.makeRequest(endpoint);

      // Erstelle Blob aus Response
      const blob = await response.blob();
      
      // Erstelle Download-Link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extrahiere Dateiname aus Response-Header oder verwende Standard
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'zeitplan.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: 'Zeitplan-PDF erfolgreich heruntergeladen'
      };

    } catch (error) {
      console.error('Fehler beim Zeitplan-PDF-Export:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Zeitplan-PDF-Export'
      };
    }
  }

  // Exportiert gefilterte Kategorienliste als PDF
  async exportCategoriesPDF(filters = {}) {
    try {
      // Baue Query-Parameter aus Filtern
      const queryParams = new URLSearchParams();
      
      if (filters.type && filters.type !== 'all') {
        queryParams.append('type', filters.type);
      }
      if (filters.ageGroup && filters.ageGroup !== 'all') {
        queryParams.append('ageGroup', filters.ageGroup);
      }
      if (filters.searchTerm) {
        queryParams.append('searchTerm', filters.searchTerm);
      }

      const endpoint = `/categories${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await this.makeRequest(endpoint);

      // Erstelle Blob aus Response
      const blob = await response.blob();
      
      // Erstelle Download-Link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Extrahiere Dateiname aus Response-Header oder verwende Standard
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'kategorienliste.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: 'PDF erfolgreich heruntergeladen'
      };

    } catch (error) {
      console.error('Fehler beim PDF-Export:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim PDF-Export'
      };
    }
  }

  // Exportiert gefilterte Kategorienliste als JSON
  async exportCategoriesJSON(filters = {}) {
    try {
      // Baue Query-Parameter aus Filtern
      const queryParams = new URLSearchParams();
      
      if (filters.type && filters.type !== 'all') {
        queryParams.append('type', filters.type);
      }
      if (filters.ageGroup && filters.ageGroup !== 'all') {
        queryParams.append('ageGroup', filters.ageGroup);
      }
      if (filters.searchTerm) {
        queryParams.append('searchTerm', filters.searchTerm);
      }

      const endpoint = `/categories/json${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
      const response = await this.makeRequest(endpoint);

      const data = await response.json();
      
      // Erstelle JSON-Datei
      const jsonString = JSON.stringify(data.data || data, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      
      // Erstelle Download-Link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Erstelle Dateiname mit Timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      const filename = `kategorienliste_${timestamp}.json`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(downloadUrl);

      return {
        success: true,
        message: 'JSON erfolgreich heruntergeladen'
      };

    } catch (error) {
      console.error('Fehler beim JSON-Export:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim JSON-Export'
      };
    }
  }

  // Importiert Kategorien aus JSON
  async importCategoriesJSON(categoriesArray) {
    try {
      // Import-Endpoint ist unter /api/categories/import, nicht unter /api/pdf-export
      const url = `${apiClient.baseUrl}/categories/import`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ categories: categoriesArray })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      return {
        success: true,
        data: data.data,
        message: data.message || 'JSON erfolgreich importiert'
      };

    } catch (error) {
      console.error('Fehler beim JSON-Import:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim JSON-Import'
      };
    }
  }

  // Cleanup temporärer PDF-Dateien (Admin-Funktion)
  async cleanupTempFiles() {
    try {
      const response = await this.makeRequest('/cleanup', {
        method: 'DELETE'
      });
      
      const data = await response.json();
      return {
        success: true,
        data: data,
        message: 'Temporäre Dateien erfolgreich bereinigt'
      };
    } catch (error) {
      console.error('Fehler beim Cleanup temporärer Dateien:', error);
      return {
        success: false,
        error: error.message || 'Fehler beim Cleanup'
      };
    }
  }
}

// Singleton-Instanz exportieren
const pdfExportService = new PDFExportService();
export default pdfExportService; 